import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import useRequiredFields from "../../../../hooks/useRequiredFields";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import ReceipeDetail from "./ReceipeDetail";

const Create = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [product, setProduct] = useState([]);
  const [rmpm, setRmpm] = useState([]);
  const [factory, setFactory] = useState({});
  const [list, setList] = useState([]);
  const { checkRequiredFields } = useRequiredFields();
  const [receipeNotFound, setReceipeNotFound] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [prevBatchSize, setPrevBatchSize] = useState(1);
  const [maxBatchSize, setMaxBatchSize] = useState(0);
  const [totalProducedBatchSize, setTotalProducedBatchSize] = useState(0);
  const [requisitionBatchSize, setRequisitionBatchSize] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [recipePayloadByProduct, setRecipePayloadByProduct] = useState({});

  const getRemainingQty = (product) => {
    const requestedQty = parseFloat(product.batch_size) || 0;
    const producedQty = parseFloat(product.total_produced_batch_size) || 0;
    return Math.max(requestedQty - producedQty, 0);
  };

  const isFinishedGoodAllowed = (product) => {
    const remainingQty = getRemainingQty(product);
    const currentBatchSize = parseFloat(product.current_batch_size ?? 0) || 0;

    return remainingQty > 0 && currentBatchSize <= remainingQty;
  };

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        // const resProduct = await Http.get("/receipe-finished-good");
        const resProduct = await Http.get(`/product-group-for-dropdown?requisition_id=${id}`);
        const products = resProduct.data
          .filter((item) => item.value !== "")
          .map((item) => ({
            ...item,
            current_batch_size: item.current_batch_size ?? getRemainingQty(item),
          }))
          .filter(isFinishedGoodAllowed);
        setProduct(resProduct.data);
        setSelectedProducts(products);

        // const resRmpm = await Http.get("/rmpm-with-stock?no_stock=1");
        const resRmpm = await Http.get("/product-wise-stock?no_stock=1&type=raw_materials-packaging_materials");
        setRmpm(resRmpm.data.item);

        // const resFactory = await Http.get("/factory");
        // setFactory(resFactory.data);

        const resFactorySelect = await Http.get("/factorySelect2");
        setFactory(resFactorySelect.data);

        const resBatchNo = await Http.get(
          "/last-code-increment-by-one/requisition_productions/batch_no/RP"
        );
        setInputs((values) => ({ ...values, ["batch_no"]: resBatchNo.data }));

        getCurrentDateInput();

        // Load recipes for all products
        await loadAllRecipes(products);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    DatatableFunc();
    fetchData();
  }, []);

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    if (name === "batch_size") {
      const newBatchSize = parseFloat(value) || 0;
      const max = parseFloat(maxBatchSize) || 0;

      if (newBatchSize > max) {
        setError((prev) => ({
          ...prev,
          batch_size: `Batch size cannot be more than ${max.toFixed(2)}`,
        }));
      } else {
        setError((prev) => {
          const newError = { ...prev };
          delete newError.batch_size;
          return newError;
        });
        // }

        const ratio = newBatchSize / (prevBatchSize || 1);
        const updatedList = list.map((item) => {
          const currentStandard = parseFloat(item.standard_qty) || 0;
          const currentQty = parseFloat(item.qty) || 0;
          return {
            ...item,
            standard_qty: (currentStandard * ratio).toFixed(3),
            qty: (currentQty * ratio).toFixed(3),
          };
        });

        setList(updatedList);
      }

      setPrevBatchSize(newBatchSize);
    }
  };

  const handleCustomInputChange = (e, index) => {
    const { name, value } = e.target; // Chanages Experience Input Name and Value
    const inputInfo = [...list]; // Defined Experience State All Information into List Variable

    inputInfo[index][name] = value;
    setList(inputInfo);
  };

  const getCurrentDateInput = () => {
    const dateObj = new Date();

    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();

    const shortDate = `${year}-${month}-${day}`;

    setInputs((values) => ({ ...values, ["date"]: shortDate }));
    setInputs((values) => ({
      ...values,
      ["expected_production_date"]: shortDate,
    }));

    const hours = ("0" + dateObj.getHours()).slice(-2);
    const minutes = ("0" + dateObj.getMinutes()).slice(-2);

    const defaultTime = `${hours}:${minutes}`;

    setInputs((values) => ({ ...values, ["time"]: defaultTime }));
  };

  const handleProductBatchSizeChange = (index, newBatchSize) => {
    const updated = [...selectedProducts];

    const changedProduct = updated[index];
    const requestedQty = parseFloat(changedProduct.batch_size) || 0;
    const producedQty = parseFloat(changedProduct.total_produced_batch_size) || 0;
    const remainingQty = requestedQty - producedQty;

    let currentBatchSize = Number(newBatchSize);

    if (isNaN(currentBatchSize) || currentBatchSize < 1) {
      currentBatchSize = 1;
      Swal.fire({
        title: "Info",
        text: "Batch size must be at least 1. Value adjusted to 1.",
        icon: "info",
        confirmButtonText: "Ok",
      });
    }

    if (currentBatchSize > remainingQty) {
      Swal.fire({
        title: "Warning!",
        text: `Batch size cannot exceed remaining limit of ${remainingQty.toFixed(2)}. It has been adjusted to ${remainingQty.toFixed(2)}.`,
        icon: "warning",
        confirmButtonText: "Ok",
      });
      currentBatchSize = remainingQty;
    }

    updated[index] = { ...updated[index], current_batch_size: currentBatchSize };

    setSelectedProducts(updated);
    loadAllRecipes(updated).catch((error) => console.error("Reload recipe error:", error));
  };

  const handleProductSelectChange = (selectedOption, index) => {
    const updated = [...selectedProducts];
    const preservedCurrent = updated[index] ? updated[index].current_batch_size : undefined;
    updated[index] = {
      ...selectedOption,
      current_batch_size:
        preservedCurrent ?? selectedOption.current_batch_size ?? getRemainingQty(selectedOption),
    };
    const validProducts = updated.filter(isFinishedGoodAllowed);
    setSelectedProducts(validProducts);
    loadAllRecipes(validProducts).catch((error) => console.error("Reload recipe error:", error));
  };

  const handleRemoveFinishedGood = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This finished good will be removed from the selection.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const updated = selectedProducts.filter((_, i) => i !== index);
      setSelectedProducts(updated);

      if (updated.length === 0) {
        setList([]);
        setRecipePayloadByProduct({});
        setRequisitionBatchSize(0);
        setTotalProducedBatchSize(0);
        setMaxBatchSize(0);
        return;
      }

      setLoading(true);
      loadAllRecipes(updated)
        .catch((error) => console.error("Reload recipe error:", error))
        .finally(() => setLoading(false));
    });
  };

  const loadAllRecipes = async (productsToLoad) => {
    let allItems = [];
    let totalReqBatchSize = 0;
    let totalProducedBatchSize = 0;
    const payloadByProduct = {};

    const recipeResults = await Promise.all(
      productsToLoad.map(async (selectedOption) => {
        const value = selectedOption.value;
        const reqBatchSize = parseFloat(selectedOption.batch_size) || 0;
        const producedBatchSize =
          parseFloat(selectedOption.total_produced_batch_size) || 0;

        totalReqBatchSize += reqBatchSize;
        totalProducedBatchSize += producedBatchSize;

        const tempInputs = { ...inputs, product_id: value, receipe_type: "fifo" };
        const res = await Http.post("/receipe-load-product-wise", tempInputs);

        if (res.data.status != 1) {
          return [];
        }

        const recipeItems = res.data.info.items;
        const defaultBatchSize = parseFloat(res.data.info.batch_size) || 1;
        const newBatchSize = parseFloat(selectedOption.current_batch_size ?? selectedOption.batch_size) || 1;

        if (recipeItems.length === 0) {
          return null;
        }

        const ratio = newBatchSize / defaultBatchSize;
        const scaledItems = recipeItems.map((item) => {
          const baseQty = parseFloat(item.qty) || 0;
          const calculatedStandard = (baseQty * ratio).toFixed(3);
          return {
            ...item,
            standard_qty: calculatedStandard,
            qty: calculatedStandard,
          };
        });

        payloadByProduct[value] = {
          product_id: value,
          receipe_id: res.data.info.id,
          batch_size: newBatchSize,
          requisition_batch_size: reqBatchSize,
          produced_batch_size: producedBatchSize,
          items: scaledItems,
        };

        return scaledItems;
      })
    );

    setRecipePayloadByProduct(payloadByProduct);

    recipeResults.flat().filter(Boolean).forEach((newItem) => {
      const existingItemIndex = allItems.findIndex(
        (item) => Number(item.product_id) === Number(newItem.product_id)
      );

      if (existingItemIndex !== -1) {
        allItems[existingItemIndex].standard_qty = (
          parseFloat(allItems[existingItemIndex].standard_qty) +
          parseFloat(newItem.standard_qty)
        ).toFixed(3);
        allItems[existingItemIndex].qty = (
          parseFloat(allItems[existingItemIndex].qty) +
          parseFloat(newItem.qty)
        ).toFixed(3);
      } else {
        allItems.push(newItem);
      }
    });

    setList(allItems);
    setRequisitionBatchSize(totalReqBatchSize);
    setTotalProducedBatchSize(totalProducedBatchSize);
    setMaxBatchSize(totalReqBatchSize - totalProducedBatchSize);
  };

  const addMoreList = (id) => {
    setList((prevList) => {
      const index = prevList.findIndex(
        (item) => Number(item.product_id) === Number(id)
      );

      if (index !== -1) {
        Swal.fire({
          title: "Error!",
          text: "This Product already in the list ! Please choose another RMPM.",
          icon: "error",
          confirmButtonText: "Ok",
        });
        return prevList;
      }
      Http.get(`/add-new-rmpm-receipe/${id}?no_stock=1`).then((res) => {
        const newItem = res.data;
        setList((current) => [
          ...current,
          {
            ...newItem,
            request_qty: 1,
            standard_qty: 1,
            qty: 1
          },
        ]);
      });

      return prevList;
    });
  };





  // Remove List
  const removeList = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const inputInfo = [...list]; // Load All Existing experience State Data
        const removedItem = inputInfo[index];
        inputInfo.splice(index, 1); // Remove Experience by Index/Click Event Wise
        setList(inputInfo); // Replace Existing experience into State

        if (removedItem) {
          setRecipePayloadByProduct((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach((key) => {
              const items = updated[key].items || [];
              const filtered = items.filter((item) => {
                if (removedItem.id && item.id && Number(item.id) === Number(removedItem.id)) {
                  return false;
                }
                if (removedItem.product_id && item.product_id && Number(item.product_id) === Number(removedItem.product_id)) {
                  return false;
                }
                return true;
              });

              if (filtered.length === 0) {
                delete updated[key];
              } else {
                updated[key] = { ...updated[key], items: filtered };
              }
            });
            return updated;
          });
        }
      }
    });
  };

  const formSubmit = () => {
    checkRequiredFields();

    // Validate each selected product's batch size
    for (let product of selectedProducts) {
      const productReq = parseFloat(product.batch_size) || 0;
      const productProduced = parseFloat(product.total_produced_batch_size) || 0;
      const productRemaining = productReq - productProduced;
      const currentBatchSize = parseFloat(product.current_batch_size) || 0;

      if (currentBatchSize > productRemaining) {
        Swal.fire({
          title: "Error!",
          text: `Batch size for ${product.label} (${currentBatchSize}) cannot exceed remaining limit (${productRemaining.toFixed(2)})`,
          icon: "error",
          confirmButtonText: "Ok",
        });
        return;
      }
    }

    // Build one submit payload per finished good
    const submitData = selectedProducts.map((product) => {
      const recipePayload = recipePayloadByProduct[product.value] || {};
      const currentBatchSize = parseFloat(product.current_batch_size) || 0;

      return {
        ...inputs,
        requisition_id: id,
        product_id: product.value,
        receipe_id: recipePayload.receipe_id || product.receipe_id || null,
        batch_size: currentBatchSize,
        requisition_batch_size: parseFloat(product.batch_size) || 0,
        produced_batch_size: parseFloat(product.total_produced_batch_size) || 0,
        items: list.map((item) => ({
          product_id: item.product_id || item.id,
          standard_qty: parseFloat(item.standard_qty) || 0,
          issue_qty: parseFloat(item.qty) || 0,
          description: item.description || "",
        })),
      };
    });

    setSubmitting(true);
    Http.post("/production", submitData)
      .then((res) => {
        setSubmitting(false);
        navigate("/production", {
          state: {
            message: res.data.message,
            type: res.data.type || "success",
          },
        });
      })
      .catch(function (e) {
        setSubmitting(false);
        const responseData = e.response?.data || {};

        if (responseData.errors) {
          setError(responseData.errors);
          return;
        }

        setError(responseData);
      });
  };
  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger 1 small">{error[fieldName]}</div>;
    }
    return null;
  };

  const getStockErrorMessage = () => {
    if (!error?.message || !error?.product_id) {
      return null;
    }

    if (Number(error.product_id) !== Number(inputs.product_id)) {
      return null;
    }

    return <div className="text-danger small">{error.message}</div>;
  };
  const cols = [

    { field: (item) => item.code + "-" + item.name, header: "Item" },
    { field: (item) => item.unit_qty + " " + item.unit_name, header: "Unit" },
    { field: "stockQty", header: "Stock" },
  ];

  const actionTemplate = (item, deleteData) => {

    return (
      <td className="text-right">
        <button
          className="btn btn-success btn-sm"
          onClick={() => addMoreList(item.id)}
        >
          <i className="fa fa-plus"></i>
        </button>
      </td>
    );
  };

  return (
    <div>
      {/* <LoaderComponent /> */}
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Generate Production by Requisition</h5>
        </div>
        <div className="am-pagebody row d-flex justify-content-evenly">
          <div className="card pd-20 pd-sm-40 col-7">
            <h6 className="card-body-title mg-sm-b-30 text-center">
              Generate Production by Requisition Form
            </h6>
            <hr></hr>
            {/* <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div> */}
            {loading ? (
              <ProgressSpinner />
            ) : (
              <div>
                <div className="row">
                  <div className="col-lg">
                    <label>
                      Date <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={inputs.date || ""}
                      onChange={handleChange}
                      placeholder="Input Date"
                      data-required
                    />

                    {displayError("date")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Time <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      name="time"
                      value={inputs.time || ""}
                      onChange={handleChange}
                      placeholder="Input Time"
                      data-required
                    />
                    {displayError("time")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Req. No. <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="batch_no"
                      className="form-control"
                      name="batch_no"
                      value={inputs.batch_no || ""}
                      onChange={handleChange}
                      placeholder="Input Batch No"
                      data-required
                    />
                    {displayError("batch_no")}
                  </div>
                </div>
                <div style={{ maxHeight: "500px", overflowY: "auto" }}
                >
                  {selectedProducts.map((item, idx) => (
                    <div className="row mt-3" key={idx}>

                      <div className="col-lg">
                        <label htmlFor="">
                          Finished Good <i className="fa fa-star required"></i>
                        </label>
                        <Select
                          className="basic-single"
                          classNamePrefix="select"
                          value={item}
                          name={`product_id_${idx}`}
                          options={product}
                          onChange={(opt) => handleProductSelectChange(opt, idx)}
                          data-required
                          isDisabled={true}
                        />
                        {displayError("product_id")}
                        {getStockErrorMessage()}
                      </div>
                      <div className="col-lg">
                        <label>
                          Current Production Qty <i className="fa fa-star required"></i>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          name={`batch_size_${idx}`}
                          value={item.current_batch_size ?? getRemainingQty(item)}
                          onChange={(e) => handleProductBatchSizeChange(idx, e.target.value)}
                          placeholder="Input Batch Size"
                          data-required
                          required
                          min={1}
                        />
                        {displayError("batch_size")}
                      </div>
                      <div className="col-lg">
                        <label>Requested Production Qty </label>
                        <input
                          type="text"
                          className="form-control"
                          value={item.batch_size}
                          disabled
                        />
                      </div>
                      <div className="col-lg">
                        <label>Already Produced</label>
                        <input
                          type="text"
                          className="form-control"
                          value={item.total_produced_batch_size}
                          disabled
                        />
                      </div>
                      <div className="col-lg d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-danger btn-block"
                          onClick={() => handleRemoveFinishedGood(idx)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}

                  {!(inputs.fg_out > 0) && (
                    <div className="row mt-3">
                      <div className="card-solid">
                        <div className="card-header with-border text-center">
                          <h5 className="card-title mb-0">Items List</h5>
                        </div>
                        <div className="card-body mt-3 p-0 pt-3" style={{ maxHeight: "500px", overflowY: "auto" }}
                        >
                          <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0">
                            <thead>
                              <tr>
                                <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  Code
                                </th>
                                <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  Product
                                </th>
                                <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  Standard Qty
                                </th>
                                <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  Request Qty
                                </th>
                                <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  Remarks
                                </th>
                                <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Array.from(list).map((x, i) => (
                                <ReceipeDetail
                                  key={i}
                                  listInfo={x}
                                  listSl={i}
                                  error={error}
                                  rmpm={rmpm}
                                  noStockCheck={1}
                                  remove={() => removeList(i)}
                                  listInputChange={(e) =>
                                    handleCustomInputChange(e, i)
                                  }
                                />
                              ))}
                              {receipeNotFound != "" && list.length == 0 ? (
                                <tr>
                                  <td colSpan={5}>
                                    <p
                                      style={{ color: "red" }}
                                      className="text-center mt-3"
                                    >
                                      {receipeNotFound}
                                    </p>
                                  </td>
                                </tr>
                              ) : (
                                ""
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="row mt-3">
                  <div className="col-3">
                    {submitting ? (
                      <div className="d-flex align-items-center mb-2">
                        <ProgressSpinner
                          style={{ width: "30px", height: "30px" }}
                          strokeWidth="4"
                        />
                        <span className="ms-2 text-primary">Submitting...</span>
                      </div>
                    ) : null}
                    <button
                      className="btn btn-primary btn-block mg-b-10"
                      onClick={formSubmit}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <i className="fa fa-spinner fa-spin mg-r-10"></i> Submitting
                        </>
                      ) : (
                        <>
                          <i className="fa fa-send mg-r-10"></i> Create
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className="card pd-20 pd-sm-40 col-4"
            style={{ backgroundColor: "#ffffffc7" }}
          >
            <h6 className="card-body-title mg-sm-b-30 text-center">
              Raw &amp; Packaging Materials
            </h6>
            <hr></hr>

            <ReactDataTable
              cols={cols}
              receiveData={rmpm}
              isLoading={loading}
              actionTemplate={actionTemplate}
            // deleteData={deleteData}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Create;

